export interface ISimulation {
    id: number;
    name: string;
    lunchTime: string;
    sleepTime: string;
    period1Start: string;
    period1End: string;
    period2Start: string | null;
    period2End: string | null;
    morningCMin: number;
    afternoonCMin: number;
    eveningCMin: number;
    morningCMax: number;
    afternoonCMax: number;
    eveningCMax: number;
    threshold: number;
    timePercentageInPeriod1: number;
    timePercentageInPeriod2: number;
    timePercentageInSleep: number;
    responderPercentageInPeriod1: number;
    responderPercentageInPeriod2: number;
    responderPercentageInSleep: number;
    rollerCoasterEffectPercentage: number;
    weight: number;
    savedSimulations: { id: number; name: string }[];
    drugs: { id: number; name: string }[];
    simulationDrugs: ISimulationDrug[];
}

export interface ISimulationDrug {
    uniqueId: string;
    drugID: number;
    drugDoseID?: number;
    drugQuantityID?: number;
    time?: string;
    meal?: boolean;
    drugDoses?: { id: number; name: string }[];
    drugQuantities?: { id: number; name: string }[];
}

export class SimulationDrug {
    uniqueId: string;
    drugID: number;
    drugDoseID?: number;
    drugQuantityID?: number;
    time?: string;
    meal?: boolean;
    drugDoses: { id: number; name: string }[];
    drugQuantities: { id: number; name: string }[];

    constructor(data: ISimulationDrug) {
        this.uniqueId = crypto.randomUUID();
        this.drugID = data.drugID;
        this.drugDoseID = data.drugDoseID;
        this.drugQuantityID = data.drugQuantityID;
        this.time = data.time;
        this.meal = data.meal;
        this.drugDoses = data.drugDoses ?? [];
        this.drugQuantities = data.drugQuantities ?? [];
    }
}

export class Simulation {
    id: number;
    name: string;
    lunchTime: string;
    sleepTime: string;
    period1Start: string;
    period1End: string;
    period2Start: string;
    period2End: string;
    morningCMin: number;
    afternoonCMin: number;
    eveningCMin: number;
    morningCMax: number;
    afternoonCMax: number;
    eveningCMax: number;
    threshold: number;
    timePercentageInPeriod1: number;
    timePercentageInPeriod2: number;
    timePercentageInSleep: number;
    responderPercentageInPeriod1: number;
    responderPercentageInPeriod2: number;
    responderPercentageInSleep: number;
    rollerCoasterEffectPercentage: number;
    weight: number;
    savedSimulations: { id: number; name: string }[];
    drugs: { value: string; label: string }[];
    savedSimulationsCombo: { value: string; label: string }[];
    simulationDrugs: ISimulationDrug[];

    constructor(data: ISimulation) {
        this.id = data.id;
        this.name = data.name;
        this.lunchTime = data.lunchTime;
        this.sleepTime = data.sleepTime;
        this.period1Start = data.period1Start;
        this.period1End = data.period1End;
        this.period2Start = data.period2Start;
        this.period2End = data.period2End;
        this.morningCMin = data.morningCMin;
        this.afternoonCMin = data.afternoonCMin;
        this.eveningCMin = data.eveningCMin;
        this.morningCMax = data.morningCMax;
        this.afternoonCMax = data.afternoonCMax;
        this.eveningCMax = data.eveningCMax;
        this.threshold = data.threshold;
        this.timePercentageInPeriod1 = data.timePercentageInPeriod1;
        this.timePercentageInPeriod2 = data.timePercentageInPeriod2;
        this.timePercentageInSleep = data.timePercentageInSleep;
        this.responderPercentageInPeriod1 = data.responderPercentageInPeriod1;
        this.responderPercentageInPeriod2 = data.responderPercentageInPeriod2;
        this.responderPercentageInSleep = data.responderPercentageInSleep;
        this.rollerCoasterEffectPercentage = data.rollerCoasterEffectPercentage;
        this.weight = data.weight;
        this.savedSimulations = data.savedSimulations;

        // Transformer `drugs` en format combobox
        this.drugs = data.drugs?.map(({ id, name }) => ({
            value: id.toString(),
            label: name,
        }));

        this.savedSimulationsCombo = data.savedSimulations?.map(({ id, name }) => ({
            value: id.toString(),
            label: name,
        }));

        this.simulationDrugs = data.simulationDrugs?.map((el) => new SimulationDrug(el));
    }
}

export interface ISimulationPayload extends Omit<ISimulation, 'id' | 'savedSimulations' | 'drugs'> {
    patientID: number;
    weight: number;
    simulationDrugs: Omit<ISimulationDrug, 'drugDoses' | 'drugQuantities'>[];
}

export interface ISimulationCurveGraph {
    curve1: number[];
    curve2: number[];
    curve3: number[];
    curve4: number[];
    curve5: number[];
    curve6: number[];
    curve7: number[];
    curve8: number[];
    curve9: number[];
    period1Percentage: number;
    period2Percentage: number;
    period3Percentage: number;
    maxYValue: number;
    rollerCoasterEffect: number;
    ngMlLunchTime: number;
    ngMlSleepTime: number;
    totalScore: number;
    xAxisStart: string;
    xAxisEnd: string;
    xAxisCurveStart: string;
    morningReponsePercentages: {
        noResponse: number;
        response: number;
        adverseResponse: number;
        reponseOrAdverseResponse: number;
        noResponseOrResponse: number;
    };
    afternoonReponsePercentages: {
        noResponse: number;
        response: number;
        adverseResponse: number;
        reponseOrAdverseResponse: number;
        noResponseOrResponse: number;
    };
    eveningReponsePercentages: {
        noResponse: number;
        response: number;
        adverseResponse: number;
        reponseOrAdverseResponse: number;
        noResponseOrResponse: number;
    };
}

export class SimulationCurveGraph implements ISimulationCurveGraph {
    curve1: number[];
    curve2: number[];
    curve3: number[];
    curve4: number[];
    curve5: number[];
    curve6: number[];
    curve7: number[];
    curve8: number[];
    curve9: number[];
    period1Percentage: number;
    period2Percentage: number;
    period3Percentage: number;
    maxYValue: number;
    rollerCoasterEffect: number;
    ngMlLunchTime: number;
    ngMlSleepTime: number;
    totalScore: number;
    xAxisStart: string;
    xAxisEnd: string;
    xAxisCurveStart: string;
    morningReponsePercentages: {
        noResponse: number;
        response: number;
        adverseResponse: number;
        reponseOrAdverseResponse: number;
        noResponseOrResponse: number;
    };
    afternoonReponsePercentages: {
        noResponse: number;
        response: number;
        adverseResponse: number;
        reponseOrAdverseResponse: number;
        noResponseOrResponse: number;
    };
    eveningReponsePercentages: {
        noResponse: number;
        response: number;
        adverseResponse: number;
        reponseOrAdverseResponse: number;
        noResponseOrResponse: number;
    };

    constructor(data: ISimulationCurveGraph) {
        this.curve1 = data.curve1 ?? [];
        this.curve2 = data.curve2 ?? [];
        this.curve3 = data.curve3 ?? [];
        this.curve4 = data.curve4 ?? [];
        this.curve5 = data.curve5 ?? [];
        this.curve6 = data.curve6 ?? [];
        this.curve7 = data.curve7 ?? [];
        this.curve8 = data.curve8 ?? [];
        this.curve9 = data.curve9 ?? [];
        this.period1Percentage = data.period1Percentage;
        this.period2Percentage = data.period2Percentage;
        this.period3Percentage = data.period3Percentage;
        this.maxYValue = data.maxYValue;
        this.rollerCoasterEffect = data.rollerCoasterEffect;
        this.ngMlLunchTime = data.ngMlLunchTime;
        this.ngMlSleepTime = data.ngMlSleepTime;
        this.totalScore = data.totalScore;
        this.xAxisStart = data.xAxisStart;
        this.xAxisEnd = data.xAxisEnd;
        this.xAxisCurveStart = data.xAxisCurveStart;
        this.morningReponsePercentages = data.morningReponsePercentages;
        this.afternoonReponsePercentages = data.afternoonReponsePercentages;
        this.eveningReponsePercentages = data.eveningReponsePercentages;
    }
}

export interface AreaChartData {
    time: string[];
    curve1: number[];
    curve2: number[];
    curve3: number[];
    curve4: number[];
    curve5: number[];
    curve6: number[];
    curve7: number[];
    curve8: number[];
    curve9: number[];
}
